import { useState, useEffect } from "react";
import { Eye, EyeOff, ArrowLeft, Mail, Lock, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "next-themes";

interface SignInPageProps {
  onNavigate: (page: string) => void;
}

export function SignInPage({ onNavigate }: SignInPageProps) {

  // const navigate = (url: string, page: string, onNavigate: (p: string) => void) => {
  //   window.history.pushState({}, "", url);
  //   onNavigate(page);
  // };

    const navigate = (url: string, page: string) => {
    window.history.pushState({}, "", url);
    onNavigate(page);
  };

  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { login, error, clearError } = useAuth();
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  useEffect(() => {
    const savedEmail = localStorage.getItem("remember_email");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setIsSubmitting(true);

    try {
      await login(email, password);

      if (rememberMe) {
        localStorage.setItem("remember_email", email);
      } else {
        localStorage.removeItem("remember_email");
      }

      window.history.replaceState({}, "", "/app");
      onNavigate("app");
    } finally {
      setIsSubmitting(false);
    }
  };

return (
  <div
    className="
      min-h-screen
      bg-gradient-to-br from-blue-50 to-indigo-100
      dark:from-slate-900 dark:to-slate-800
    "
  >
    {/* ================= FULL WIDTH HEADER ================= */}
    <header
      className="
        w-full border-b sticky top-0 z-50
        bg-white/80 dark:bg-slate-900/80
        backdrop-blur
        border-border
      "
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <button
          onClick={() => navigate("/", "landing")}
          className="flex items-center"
        >
          <img src={logoSrc} alt="Swimnerd Logo" className="h-10 w-auto" />
        </button>

        <nav className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/find-pool", "find-pool")}
          >
            Find Pool
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate("/blog", "blog")}
          >
            Blog
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate("/signin", "signin")}
          >
            Sign In
          </Button>

          <Button
            onClick={() => navigate("/signup", "signup")}
          >
            Get Started
          </Button>
        </nav>
      </div>
    </header>
    {/* ================= END HEADER ================= */}

    {/* ================= CENTERED SIGN-IN CONTENT ================= */}
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Sign in to your account to continue
          </p>
        </div>

        <Card className="bg-white dark:bg-slate-900 border border-border">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">
              Sign In
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    placeholder="Enter your email"
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    placeholder="Enter your password"
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  <Label className="text-sm">Remember me</Label>
                </div>

                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-sm"
                  onClick={() => onNavigate("forgot-password")}
                >
                  Forgot password?
                </Button>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Don’t have an account?{" "}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-sm"
                  onClick={() => navigate("/signup", "signup")}
                >
                  Sign up here
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/", "landing")}
            className="flex items-center gap-2 mx-auto text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </div>

      </div>
    </main>
    {/* ================= END CONTENT ================= */}
  </div>
);
}
